import * as Sentry from "@sentry/react";
import { AppDispatch, RootState } from "@Reducers/index";
import axiosWrapper from "@AxiosWrapper";
import { getBackendUrl } from "@Utils";
import { setUserTagLoading, setUserTagData, setUpdatedUserProfileDetails } from "../reducers/userTagData";
import type { GetUserDetailsByIdRequest, UserTagProfileData } from "@ServerTypes/accounts/accounts.schema";
import type { Exact } from "@Helpers/types/exact";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import { DEFAULT_SYSTEM_USER } from "@ServerTypes/helpers/systemUser.constants";

/**
 * The route's own `getUserDetailsByIdResponse` DOES describe the envelope (that was a stale note
 * here). This local type survives for a different reason: the row is declared TWICE server-side —
 * `userDetailsByIdRow` and `userTagProfileDataSchema`, both the same `account.helper.ts` projection,
 * disagreeing on nullability and on which keys exist — and the redux `userTagData` slice is typed
 * against the second. Switching to the generated envelope means collapsing those two first.
 */
type GetUserDetailsByIdBody = { status?: boolean; result?: UserTagProfileData };

export const fetchUserTagData = (id: ObjectIdString) => async (dispatch: AppDispatch, getState: () => RootState) => {
  // "0" is the stored system-user sentinel (userId.schema.ts); it has no account to look up
  if (id === DEFAULT_SYSTEM_USER) return;
  const { userTagData } = getState();

  if (userTagData[id]?.isLoading || userTagData[id]?.isLoaded) {
    return;
  }

  dispatch(setUserTagLoading({ id }));

  try {
    // Fetch user details
    const response = await getUserDetailsById({ userId: id });
    const userDetails = response.data.result;

    // If there's a profile picture to fetch, get the image as a blob
    if (userDetails?.profilePic) {
      const imgResponse = await axiosWrapper.get<Blob>(`${getBackendUrl()}/fileSystemServer/getImage/profile/${id}`, {
        responseType: "blob",
      });

      // Use FileReader to convert blob to Base64
      const reader = new FileReader();

      reader.onloadend = function () {
        // `readAsDataURL` resolves to a data-URL string; anything else means the read failed, and
        // `UserTagProfileData.profilePic` is nullable, so that case stores no picture.
        const profilePic = typeof reader.result === "string" ? reader.result : null;

        // Dispatch the data with the Base64 image URL
        dispatch(setUserTagData({ id, data: { ...userDetails, profilePic } }));
      };

      // Read the Blob as Data URL
      reader.readAsDataURL(imgResponse.data);
    } else {
      // If there's no profile picture, just dispatch the data without it
      dispatch(setUserTagData({ id, data: { ...(userDetails ?? {}), profilePic: null } }));
    }
  } catch (error) {
    console.error("Error fetching user tag data:", error);
    // The rejected promise this used to build was only ever seen by the global
    // unhandled-rejection handler; reporting the bound error directly says the same thing.
    Sentry.captureException(error);
  }
};

export const updateUserProfileDetails = (data: Record<string, unknown>) => (dispatch: AppDispatch) => {
  dispatch(setUpdatedUserProfileDetails(data));
};

export async function getUserDetailsById<T extends Exact<GetUserDetailsByIdRequest, T>>(data: T) {
  return await axiosWrapper.post<GetUserDetailsByIdBody>(`${getBackendUrl()}/accounts/getUserDetailsById`, data);
}
